/**
 * Browser Push Notifications Service
 * Handles requesting permission and showing desktop notifications
 */

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.log('Browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

// Show a desktop notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported()) {
    console.log('Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return null;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    tag: 'virtualfit-notification',
    renotify: true,
    requireInteraction: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    // Handle click - navigate to relevant page
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      if (options.data && options.data.url) {
        window.location.href = options.data.url;
      }
      notification.close();
    };
    
    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};

// Show chat message notification
export const showChatNotification = (senderName, message, onClickUrl = '/collaborations') => {
  const notification = showNotification(`New message from ${senderName}`, {
    body: message.length > 100 ? message.substring(0, 100) + '...' : message,
    tag: 'virtualfit-chat',
    data: { url: onClickUrl }
  });

  if (notification) {
    notification.onclick = () => {
      window.focus();
      window.location.href = onClickUrl;
      notification.close();
    };
  }

  return notification;
};

// Check if user has granted notification permission
export const hasNotificationPermission = () => {
  if (!isNotificationSupported()) return false;
  return Notification.permission === 'granted';
};

// Get current notification permission status
export const getNotificationPermissionStatus = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};
